import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { usePlatformCatalogAccess } from 'src/graphql/entities/group-rules/platform-catalog-access';
import { QueryVariables, Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';

export const USER_GROUP_FIELDS = gql`
  fragment UserGroupFields on UserGroup {
    id
    name
    isPublic
    ruleValues {
      id
      userGroupRuleId
      value
    }
  }
`;

const GET_USER_GROUP_VALUES_QUERY = gql`
  query GetUserGroup($params: QueryParams) @entity(name: "UserGroup") {
    getUserGroup(queryParams: $params) {
      records {
        ...UserGroupFields
      }
    }
  }
  ${USER_GROUP_FIELDS}
`;

interface GetUserGroupQuery {
  getUserGroup: Records<{
    id: string;
    name: string;
  }>;
}

export const useUserGroups = (options?: QueryHookOptions<GetUserGroupQuery>) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { includePlatformLevelData } = usePlatformCatalogAccess(
    'UserGroup',
    selectedPartnerId,
  );

  const { data, ...rest } = useQuery<GetUserGroupQuery, QueryVariables>(
    GET_USER_GROUP_VALUES_QUERY,
    {
      ...options,
      variables: {
        params: {
          limit: MAX_QUERY_LIMIT,
        },
      },
      context: {
        includePlatformLevelData,
      },
    },
  );

  const userGroups = data?.getUserGroup.records;

  return { userGroups, ...rest };
};

export function useUserGroupOptions(options?: QueryHookOptions<GetUserGroupQuery>) {
  const { userGroups, loading } = useUserGroups(options);

  return useMemo(() => {
    if (!userGroups) return { userGroupOptions: [], loading };
    const userGroupOptions = userGroups.map((rule) => ({
      id: rule.id,
      value: rule.id,
      label: rule.name,
    }));

    return { userGroupOptions, loading };
  }, [loading, userGroups]);
}
